
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useAuth } from "@/contexts/AuthContext";
import { Navigate } from "react-router-dom";

// Define testimonial data
const testimonials = [
  {
    name: "Jacob Tartabini",
    title: "Brain Creator",
    text: "I created Brain to organize my thoughts and ideas in one place. It's become my second brain!"
  },
  {
    name: "Sarah Johnson",
    title: "Knowledge Worker",
    text: "Brain helps me connect the dots between different pieces of information. I love how I can organize everything!"
  },
  {
    name: "Michael Chen",
    title: "Researcher",
    text: "I use Brain to store research findings, references, and insights. It's transformed how I work."
  }
];

// Define feature data
const features = [
  {
    icon: "📝",
    title: "Rich Notes",
    description: "Create beautiful rich text notes with images, links, and formatting"
  },
  {
    icon: "🔍",
    title: "Fast Search", 
    description: "Quickly find what you're looking for with powerful search capabilities"
  },
  {
    icon: "🔒",
    title: "Privacy Controls",
    description: "Keep your notes private or share them securely with password protection"
  },
  {
    icon: "🌙",
    title: "Dark Mode",
    description: "Easy on the eyes with both light and dark themes"
  }
];

const Index = () => {
  const [animatedText, setAnimatedText] = useState("notes");
  const [animationState, setAnimationState] = useState("visible");
  const { user, loading } = useAuth();
  
  // Enhanced text animation for the headline with fade-up transition
  useEffect(() => {
    const texts = ["notes", "ideas", "knowledge", "thoughts", "insights"];
    let currentIndex = 0;
    
    const intervalId = setInterval(() => {
      // Start fade-out animation
      setAnimationState("hidden");
      
      // Change text and fade back in after a short delay
      setTimeout(() => {
        currentIndex = (currentIndex + 1) % texts.length;
        setAnimatedText(texts[currentIndex]);
        setAnimationState("visible");
      }, 500); // Half of the interval for a smooth transition
      
    }, 3000);
    
    return () => clearInterval(intervalId);
  }, []);
  
  // Auto-redirect if user is logged in - moved AFTER all hooks are called
  if (!loading && user) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-b from-background to-muted">
      {/* Hero section */}
      <section className="flex flex-col items-center justify-center py-20 px-4">
        <div className="text-center max-w-3xl mx-auto">
          <div className="mb-6 text-lg md:text-2xl font-medium text-primary">
            Welcome to
          </div>
          <h1 className="text-4xl sm:text-6xl md:text-7xl font-extrabold tracking-tight mb-6">
            Brain <span className="text-primary">🧠</span>
          </h1>
          <div className="h-20">
            <p className="text-xl sm:text-2xl md:text-3xl text-muted-foreground mb-8">
              Your personal knowledge base for storing and organizing <br />
              <span 
                key={animatedText} 
                className={`text-primary font-medium inline-block transition-all duration-500 transform
                  ${animationState === "visible" ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}
              >
                {animatedText}
              </span>
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button asChild size="lg" className="text-md">
              <Link to="/auth">Get Started</Link>
            </Button>
            <Button variant="outline" size="lg" asChild className="text-md">
              <Link to="/auth">Sign In</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Features section */}
      <section className="py-16 px-4 bg-card">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-12">Features</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <Card key={index} className="border-0 shadow-sm">
                <CardHeader className="pb-2">
                  <div className="text-3xl mb-2">{feature.icon}</div>
                  <CardTitle className="text-xl">{feature.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground">{feature.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-16 px-4">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-12">What Users Say</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <Card key={index} className="bg-background">
                <CardContent className="pt-6">
                  <p className="italic mb-4">"{testimonial.text}"</p>
                  <div>
                    <p className="font-medium">{testimonial.name}</p>
                    <p className="text-sm text-muted-foreground">{testimonial.title}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA section */}
      <section className="py-16 px-4 text-center bg-primary/10">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-3xl font-bold mb-4">Ready to organize your brain?</h2>
          <p className="text-xl text-muted-foreground mb-8">
            Join today and start building your personal knowledge base
          </p>
          <Button asChild size="lg" className="text-md">
            <Link to="/auth">Get Started for Free</Link>
          </Button>
        </div>
      </section>

      <footer className="py-8 text-center text-sm text-muted-foreground">
        <p>Jacob Tartabini's Brain – A personal hub of knowledge</p>
        <p className="mt-2">© {new Date().getFullYear()} Jacob Tartabini. All rights reserved.</p>
      </footer>
    </div>
  );
};

export default Index;
